import { User, Flame, Star, Video, Pentagon, Truck } from 'lucide-react';

import { createRoot } from 'react-dom/client';
import { MapPin } from 'lucide-react';
import mapboxgl from 'mapbox-gl';

export function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const searchIconMap = {
  user: { icon: User, color: '#4A90E2' },
  incident: { icon: Flame, color: '#D0021B' },
  poi: { icon: Star, color: '#6B8E23' },
  camera: { icon: Video, color: '#C67C00' },
  geofence: { icon: Pentagon, color: '#5B3C9D' },
};

export async function geocodeAddress(addressText, accessToken) {
  const query = encodeURIComponent(addressText);

  const res = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${accessToken}`
  );

  const data = await res.json();

  const feature = data.features?.[0];
  if (!feature || feature.relevance < 0.6) {
    console.error('Address not found');
    return null;
  }

  const result = {
    country: null,
    city: null,
    street: null,
  };

  // Street-level text (e.g., "213 Guillemard Road")
  if (feature.place_type?.includes('address')) {
    result.street = feature.text;
  }

  // Check context array for city and country
  for (const item of feature.context || []) {
    if (item.id.startsWith('place.')) result.city = result.city || item.text;
    if (item.id.startsWith('country.')) result.country = result.country || item.text;
  }

  // Fallback: try feature-level fields if context is incomplete
  if (!result.city && feature.place_type?.includes('place')) result.city = feature.text;
  if (!result.country && feature.place_type?.includes('country')) result.country = feature.text;

  return {
    lat: feature.center[1],
    lng: feature.center[0],
    display_name: feature.place_name,
    city: result.city || '',
    country: result.country || '',
    street: result.street || '',
  };
}

export function flyToAndShowPin(lat, lon, zoomLevel, map, funcPostToPopMap) {
  if (!lat || !lon) return;
  const options = {
    center: [lon, lat],
    speed: 2.5,
    curve: 1.42,
    essential: true,
  };
  if (typeof zoomLevel === 'number') options.zoom = zoomLevel;
  map.flyTo(options);
  showTempPin(lat, lon, map);
  if (funcPostToPopMap) funcPostToPopMap({ type: 'flyToAndShowPin', payload: { lat, lon, zoomLevel } });
}

export function showTempPin(lat, lon, map) {
  const el = document.createElement('div');

  // Lucide uses 'size' prop. 28px is roughly size={28}
  const root = createRoot(el);
  root.render(
    <MapPin
      size={28}
      color="#3B82F6"
      strokeWidth={2.5}
      style={{
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
        animation: 'dropPin 0.6s ease-out',
        transformOrigin: 'bottom',
      }}
    />
  );

  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom', // Sets the tip of the pin as the anchor point
    offset: [0, 0], // Adjust if needed based on the animation
  })
    .setLngLat([lon, lat])
    .addTo(map);

  setTimeout(() => {
    marker.remove();
    root.unmount(); // Clean up the React root to prevent memory leaks
  }, 2000);
}

export function showMarker({
  category,
  item,
  color,
  showLabel = true,
  breathing = false,
  afterDragFunc,
  map,
  markersObj,
  showCameraStreamPopup,
  funcPostToPopMap,
}) {
  if (funcPostToPopMap) {
    funcPostToPopMap({
      type: 'showMarker',
      payload: {
        category,
        item,
        color,
        showLabel,
        breathing,
        hasAfterDragFunc: afterDragFunc !== null,
        hasShowCameraStreamPopup: showCameraStreamPopup !== null,
      },
    });
  }

  const markers = markersObj[category];
  let IconComponent = null;
  let iconColor = color;
  let lng = null;
  let lat = null;
  let offsetY = 0;
  let iconSize = 24;
  if (category === 'camera') {
    if (!item.location?.[0]) return;

    const config = searchIconMap['camera'];
    IconComponent = config.icon;
    iconColor = iconColor || config.color;

    lng = item.location[1];
    lat = item.location[0];
    offsetY = 7;
  } else if (category == 'poi') {
    if (!item.location?.[0]) return;
    const config = searchIconMap['poi'];
    IconComponent = config.icon;
    iconColor = iconColor || config.color;
    lng = item.location[0];
    lat = item.location[1];
    offsetY = 7;
  } else if (category == 'editingPoi') {
    if (!item.location?.[0]) return;
    const config = searchIconMap['poi'];
    IconComponent = config.icon;
    iconColor = iconColor || config.color;
    lng = item.location[0];
    lat = item.location[1];
    offsetY = 7;
    iconSize = 32;
  }

  // remove existing first (just in case)
  const old = markers.get(item.id);
  if (old) {
    const { lng: oldlng, lat: oldlat } = old.getLngLat();
    const distance = getDistance(lat, lng, oldlat, oldlng);
    if (distance < 5) return; //it is not necessary to refresh if the distance does not change much
    old.remove();
  }

  const el = document.createElement('div');
  el.className = 'flex flex-col items-center justify-center';
  el.style.lineHeight = '1.2';
  const root = createRoot(el);
  root.render(
    <div className="flex flex-col items-center">
      {IconComponent && (
        <IconComponent
          size={iconSize}
          color={iconColor}
          strokeWidth={2.5}
          className={breathing ? 'transition-transform duration-150 animate-breath' : ''}
        />
      )}
      {showLabel && (
        <div className="font-semibold text-[12px] mt-0.5 whitespace-nowrap" style={{ color: iconColor }}>
          {item.displayName}
        </div>
      )}
    </div>
  );

  const marker = new mapboxgl.Marker({
    element: el,
    draggable: typeof afterDragFunc === 'function',
    offset: [0, offsetY],
  })
    .setLngLat([lng, lat])
    .addTo(map);

  marker.getElement().addEventListener('pointerdown', e => {
    e.stopPropagation();
    if (category === 'camera') {
      showCameraStreamPopup(item);
    } else {
      if (item?.onClick) item.onClick(item);
    }
  });

  marker._reactRoot = root;
  markers.set(item.id, marker);

  if (afterDragFunc) {
    marker.on('dragend', async () => {
      const { lat, lng } = marker.getLngLat();
      await afterDragFunc({ lat, lng });
    });
  }
}

export function removeMarker({ category, id, markersObj, funcPostToPopMap }) {
  if (funcPostToPopMap) {
    funcPostToPopMap({
      type: 'removeMarker',
      payload: { category, id },
    });
  }
  const markers = markersObj[category];
  if (!markers) return;

  const marker = markers.get(id);
  if (marker) {
    if (marker._reactRoot) {
      // ⚠️ guess/assumption: Using setTimeout(0) to push unmount to the next event loop tick
      const rootToUnmount = marker._reactRoot;
      //use a settimeout 0 is for avoiding the warning: Attempted to synchronously unmount a root while React was already rendering. React cannot finish unmounting the root until the current render has completed, which may lead to a race condition.
      setTimeout(() => {
        try {
          rootToUnmount.unmount();
        } catch (e) {
          console.warn('Failed to unmount marker root:', e);
        }
      }, 0);
    }
    marker.remove();
    markers.delete(id);
  }
}

export function showGeofence({ item, coordinates, map, mapLinesObj, funcPostToPopMap }) {
  if (funcPostToPopMap) {
    funcPostToPopMap({
      type: 'showGeofence',
      payload: { item, coordinates },
    });
  }

  const polygons = coordinates || item?.coordinates;
  if (!Array.isArray(polygons) || polygons.length === 0) return;
  const features = polygons.map((ring, idx) => ({
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: ring,
    },
    properties: {
      category: 'geofence',
      geofenceId: item.id,
      segmentIndex: idx,
    },
  }));

  mapLinesObj.geofence.set(
    item.id,
    features.map(f => ({ feature: f }))
  );
  updateGeofenceSource(map, mapLinesObj);
}

export function removeGeofence({ id, map, mapLinesObj, funcPostToPopMap }) {
  if (funcPostToPopMap) {
    funcPostToPopMap({
      type: 'removeGeofence',
      payload: { id },
    });
  }
  mapLinesObj.geofence.delete(id);
  updateGeofenceSource(map, mapLinesObj);
}

export function updateGeofenceSource(map, mapLinesObj) {
  if (!map) return;
  const source = map.getSource('geofence');
  if (!source) return;

  const features = [...mapLinesObj.geofence.values()].flatMap(list => list.map(entry => entry.feature));

  source.setData({
    type: 'FeatureCollection',
    features,
  });
}
